// Strip trailing entity-type suffixes that AI responses sometimes append to
// the same park, so that "Dead Horse Ranch State Park" and "Dead Horse Ranch
// State Park Campground" collapse to a single canonical key for grouping.
// Conservative — only matches a single trailing token and keeps the
// remainder, so "Verde Ranch RV Resort" and "Verde Ranch RV Resort &
// Cottages" stay distinct (they're different properties).
const TRAILING_PARK_SUFFIX = /\s+(?:campground|campgrounds|rv\s+park|rv\s+parks|rv\s+resort|rv\s+resorts)$/i;

export function canonicalizeParkName(name) {
  if (!name) return '';
  const trimmed = String(name).trim();
  const stripped = trimmed.replace(TRAILING_PARK_SUFFIX, '').trim();
  return stripped.toLowerCase().replace(/\s+/g, ' ');
}

// Merge competitor threat rows that share a canonical name. Picks the most
// formal-looking display name (longer string wins, since "Dead Horse Ranch
// State Park Campground" → strips → matches "Dead Horse Ranch State Park",
// but the State Park version is the real entity). Sums mentions, takes max
// threat_score / providers / displacement, min avg_position. Collected
// aliases are surfaced as a tooltip on the row.
export function mergeAliasedThreatRows(rawRows) {
  const groups = new Map();
  for (const r of rawRows) {
    const key = canonicalizeParkName(r.park_name);
    if (!key) continue;
    if (!groups.has(key)) {
      groups.set(key, { rows: [r] });
    } else {
      groups.get(key).rows.push(r);
    }
  }
  return Array.from(groups.values()).map(({ rows }) => {
    if (rows.length === 1) return rows[0];
    // Pick the display name with the highest mentions — that's the canonical
    // one users see most. Ties broken by shortest name (less suffix noise).
    const displayRow = [...rows].sort((a, b) => {
      const m = Number(b.mentions ?? 0) - Number(a.mentions ?? 0);
      if (m !== 0) return m;
      return (a.park_name || '').length - (b.park_name || '').length;
    })[0];
    const aliases = rows
      .map((r) => r.park_name)
      .filter((n) => n && n !== displayRow.park_name);
    const sumMentions = rows.reduce((s, r) => s + Number(r.mentions ?? 0), 0);
    const maxThreat = Math.max(...rows.map((r) => Number(r.threat_score ?? 0)));
    const minAvgPos = rows
      .map((r) => (r.avg_position != null ? Number(r.avg_position) : null))
      .filter((n) => n != null)
      .reduce((min, n) => (min == null || n < min ? n : min), null);
    const maxProviders = Math.max(...rows.map((r) => Number(r.providers ?? 0)));
    const maxDisplacement = Math.max(...rows.map((r) => Number(r.displacement ?? 0)));
    const sumIntrusions = rows.reduce((s, r) => s + Number(r.brand_intrusions ?? 0), 0);
    return {
      ...displayRow,
      mentions: sumMentions,
      threat_score: maxThreat,
      avg_position: minAvgPos,
      providers: maxProviders,
      displacement: maxDisplacement,
      brand_intrusions: sumIntrusions,
      _aliases: aliases,
    };
  });
}
